import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ContentServicesDashboard } from './ContentServicesDashboard';
import { RiskMitigationBox } from '../components/RiskMitigationBox';
import { getCurrentQuarterIndex, getQuarterStatus } from '../utils/fiscalYear';
import api from '../services/api';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `£${Math.round(value / 1000)}K`;
  return `£${value}`;
};

// SaaS Vertical Quarterly Chart Component with dynamic bars based on quarter status
const SaaSQuarterlyChart: React.FC<{
  quarter: string;
  budget: number;
  closed: number;
  pipeline: number;
  quarterStatus: 'past' | 'current' | 'future';
}> = ({ quarter, budget, closed, pipeline, quarterStatus }) => {
  // Calculate values based on quarter status
  // Past: show closed only, Current: show closed + pipeline stacked, Future: show pipeline only
  const secondBarValue = quarterStatus === 'past' ? closed :
                         quarterStatus === 'future' ? pipeline :
                         closed + pipeline;

  const maxValue = Math.max(budget, secondBarValue, 1);
  const budgetHeight = (budget / maxValue) * 100;
  const closedHeight = (closed / maxValue) * 100;
  const pipelineHeight = (pipeline / maxValue) * 100;

  const isOnTrack = secondBarValue >= budget * 0.95;

  // Determine label for second bar
  const getSecondBarLabel = () => {
    if (quarterStatus === 'past') return { label: 'Closed', color: 'text-green-400' };
    if (quarterStatus === 'future') return { label: 'Pipeline', color: 'text-orange-400' };
    return { label: 'Closed/Pipeline', color: 'text-white' };
  };

  const secondBarInfo = getSecondBarLabel();

  return (
    <div className="bg-black border border-white rounded-lg p-4">
      <h3 className="text-white font-bold text-lg mb-4 text-center">{quarter}</h3>

      {/* Vertical Bar Chart */}
      <div className="flex justify-center items-end gap-4 h-40 mb-4">
        {/* Budget Bar */}
        <div className="flex flex-col items-center">
          <span className="text-white text-xs font-bold mb-1">{formatCurrency(budget)}</span>
          <div className="w-12 h-32 bg-gray-800 rounded relative flex items-end">
            <div
              className="w-full bg-blue-500 rounded transition-all duration-500"
              style={{ height: `${budgetHeight}%` }}
            />
          </div>
          <span className="text-blue-400 text-xs font-semibold mt-2">Budget</span>
        </div>

        {/* Second Bar - varies based on quarter status */}
        <div className="flex flex-col items-center">
          <span className="text-white text-xs font-bold mb-1">{formatCurrency(secondBarValue)}</span>
          <div className="w-12 h-32 bg-gray-800 rounded relative flex flex-col justify-end">
            {quarterStatus === 'past' && (
              <div
                className="w-full bg-green-500 rounded transition-all duration-500"
                style={{ height: `${closedHeight}%` }}
              />
            )}
            {quarterStatus === 'current' && (
              <>
                <div
                  className="w-full bg-orange-500 rounded-t transition-all duration-500"
                  style={{ height: `${pipelineHeight}%` }}
                />
                <div
                  className="w-full bg-green-500 rounded-b transition-all duration-500"
                  style={{ height: `${closedHeight}%` }}
                />
              </>
            )}
            {quarterStatus === 'future' && (
              <div
                className="w-full bg-orange-500 rounded transition-all duration-500"
                style={{ height: `${pipelineHeight}%` }}
              />
            )}
          </div>
          <span className={`${secondBarInfo.color} text-xs font-semibold mt-2 text-center`}>{secondBarInfo.label}</span>
        </div>
      </div>

      {/* Status Badge */}
      <div className="text-center">
        <span className={`inline-block px-3 py-1 rounded text-white text-xs font-bold ${
          isOnTrack ? 'bg-green-500' : 'bg-orange-500'
        }`}>
          {isOnTrack ? 'On Track' : 'Below Target'}
        </span>
      </div>
    </div>
  );
};

// Track Status Badge Component
const TrackStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = {
    ON_TRACK: 'bg-green-500',
    OFF_TRACK: 'bg-orange-500',
    NEEDS_ATTENTION: 'bg-red-500',
  };
  const labels = {
    ON_TRACK: 'On Track',
    OFF_TRACK: 'Off Track',
    NEEDS_ATTENTION: 'Needs Attention',
  };
  return (
    <span className={`inline-block px-3 py-1 rounded text-white text-sm font-semibold ${colors[status as keyof typeof colors]}`}>
      {labels[status as keyof typeof labels]}
    </span>
  );
};

export const IndividualDashboard: React.FC = () => {
  const { repId } = useParams<{ repId: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!repId) {
        setError('No rep ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/sales-rep-dashboard/${repId}`);
        setData(response.data.data);
      } catch (err) {
        setError('Failed to load dashboard');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [repId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-xl">{error || 'Failed to load dashboard'}</p>
        </div>
      </div>
    );
  }

  // Check if this is a Content Services dashboard
  if (data.dashboardType === 'Content Services') {
    return <ContentServicesDashboard data={data} />;
  }

  const currentQuarter = getCurrentQuarterIndex();

  return (
    <div className="bg-background">
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">{data.rep.fullName}</h1>
          <p className="text-gray-400">{data.rep.department} - Sales Rep Dashboard</p>
        </div>

        {/* Full Year Summary - Horizontal Bar Chart */}
        <section className="mb-12">
          <div className="bg-black border-2 border-green-500 rounded-lg p-6">
            <h2 className="text-xl font-bold text-green-500 mb-6 text-center">FY26 Full Year Summary</h2>
            {(() => {
              const totalBudget = data.quarterlySummary?.reduce((sum: number, q: any) => sum + q.budget, 0) || 0;
              const totalClosed = data.quarterlySummary?.reduce((sum: number, q: any) => sum + q.closed, 0) || 0;
              const totalPipeline = data.quarterlySummary?.reduce((sum: number, q: any) => sum + q.pipeline, 0) || 0;
              const totalProgress = totalClosed + totalPipeline;
              const maxValue = Math.max(totalBudget, totalProgress, 1);
              const budgetWidth = (totalBudget / maxValue) * 100;
              const closedWidth = (totalClosed / maxValue) * 100;
              const pipelineWidth = (totalPipeline / maxValue) * 100;
              const isOnTrack = totalProgress >= totalBudget * 0.95;

              return (
                <div className="space-y-6">
                  {/* Budget Bar */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-blue-400 text-sm font-semibold">Budget</span>
                      <span className="text-white text-sm font-bold">{formatCurrency(totalBudget)}</span>
                    </div>
                    <div className="h-10 bg-gray-800 rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded transition-all duration-500"
                        style={{ width: `${budgetWidth}%` }}
                      />
                    </div>
                  </div>

                  {/* Closed + Pipeline Stacked Bar */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <div className="flex items-center gap-4">
                        <span className="text-green-400 text-sm font-semibold">Closed</span>
                        <span className="text-white text-sm">{formatCurrency(totalClosed)}</span>
                        <span className="text-gray-500">+</span>
                        <span className="text-orange-400 text-sm font-semibold">Pipeline</span>
                        <span className="text-white text-sm">{formatCurrency(totalPipeline)}</span>
                      </div>
                      <span className="text-white text-sm font-bold">= {formatCurrency(totalProgress)}</span>
                    </div>
                    <div className="h-10 bg-gray-800 rounded overflow-hidden flex">
                      <div
                        className="h-full bg-green-500 transition-all duration-500"
                        style={{ width: `${closedWidth}%` }}
                      />
                      <div
                        className="h-full bg-orange-500 transition-all duration-500"
                        style={{ width: `${pipelineWidth}%` }}
                      />
                    </div>
                  </div>

                  {/* Status indicator */}
                  <div className="flex justify-center">
                    <span className={`inline-block px-4 py-2 rounded text-white text-sm font-bold ${
                      isOnTrack ? 'bg-green-500' : 'bg-orange-500'
                    }`}>
                      {isOnTrack ? 'On Track' : 'Below Target'} ({Math.round((totalProgress / totalBudget) * 100)}% of Budget)
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* Quarterly Bar Charts - FY26 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">FY26 Quarterly Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.dealOverview.quarters.map((quarter: any, index: number) => (
              <SaaSQuarterlyChart
                key={quarter.quarter}
                quarter={quarter.quarter}
                budget={quarter.budget}
                closed={quarter.closed}
                pipeline={quarter.pipeline}
                quarterStatus={getQuarterStatus(index, currentQuarter)}
              />
            ))}
          </div>
        </section>

        {/* Risk and Mitigation Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Risks & Mitigations</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RiskMitigationBox
              title="Risks"
              items={data.riskItems || []}
              type="risk"
            />
            <RiskMitigationBox
              title="Mitigations"
              items={data.mitigationItems || []}
              type="mitigation"
            />
          </div>
        </section>

        {/* Deal Overview Table */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Deal Overview</h2>
          <div className="bg-black border border-white rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Quarter</th>
                  <th className="px-6 py-4 text-right text-white font-semibold">Budget</th>
                  <th className="px-6 py-4 text-right text-white font-semibold">Pipeline</th>
                  <th className="px-6 py-4 text-right text-white font-semibold">Closed</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">Track Status</th>
                </tr>
              </thead>
              <tbody>
                {data.dealOverview.quarters.map((quarter: any, index: number) => (
                  <tr
                    key={quarter.quarter}
                    className={`border-t border-gray-800 ${index === currentQuarter ? 'bg-gray-900' : ''}`}
                  >
                    <td className="px-6 py-4 text-white font-semibold">
                      {quarter.quarter}
                      {index === currentQuarter && <span className="ml-2 text-xs text-gray-400">(Current)</span>}
                    </td>
                    <td className="px-6 py-4 text-right text-white">{formatCurrency(quarter.budget)}</td>
                    <td className="px-6 py-4 text-right text-white">{formatCurrency(quarter.pipeline)}</td>
                    <td className="px-6 py-4 text-right text-white">{formatCurrency(quarter.closed)}</td>
                    <td className="px-6 py-4 text-center">
                      <TrackStatusBadge status={quarter.trackStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Current Deals */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">
            Current Deals ({data.currentDeals?.length || 0})
          </h2>
          {!data.currentDeals || data.currentDeals.length === 0 ? (
            <div className="bg-black border border-white rounded-lg p-8 text-center">
              <p className="text-gray-400">No current deals</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.currentDeals.map((deal: any) => (
                <div key={deal.id} className="bg-black border border-white rounded-lg p-5 hover:border-gray-400 transition-colors">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-white font-bold text-lg flex-1">{deal.dealName}</h3>
                    <TrackStatusBadge status={deal.trackStatus} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Value:</span>
                      <span className="text-white font-semibold">{formatCurrency(deal.contractValue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Confidence:</span>
                      <span className="text-white font-semibold">{deal.confidenceLevel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Status:</span>
                      <span className="text-white">{deal.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Timeline:</span>
                      <span className="text-white">{deal.timeline}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      </main>
    </div>
  );
};
